import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { withRouter } from 'react-router';
import RouterPropTypes from 'react-router-prop-types';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import classNames from 'classnames';
import useBarcodeDetector, {
  ERROR_STATES,
} from '../../../../hooks/search/useBarcodeDetector';
import globalTranslations from '../../../../i18n/globalTranslations';
import { modelOf } from '../../../../prop-types';
import RouteService from '../../../../services/RouteService';
import UIStore from '../../../../store/UIStore';
import Paths from '../../../../types/Paths';
import RequestState from '../../../../types/RequestState';
import { addParametersToPath } from '../../../../util/queryString';
import Icon from '../../../common/Icon';
import ErrorHandler from '../../../loader/ErrorHandler';

const BarcodeSearchModal = ({ uiStore, routeService, history, intl }) => {
  /**
   * @param {DetectedBarcode} detectedBarcode
   */
  const getPath = (detectedBarcode) => {
    if (!detectedBarcode) {
      return;
    }

    const url = routeService.getPath(
      addParametersToPath(Paths.AdvancedSearch, {
        keywords: detectedBarcode.rawValue,
        focusQuantity: 1,
      })
    );

    uiStore.barcodeReaderModal.toggle();
    navigateTo(url);
  };

  const navigateTo = (url) => history.push(url);

  const [initState] = useBarcodeDetector(getPath);

  const renderCloseButton = () => {
    return (
      <Button
        className="BarcodeSearchModal__close-button"
        onClick={uiStore.barcodeReaderModal.toggle}
        color="primary"
        aria-label={intl.formatMessage(globalTranslations.closeTitle)}
      >
        <span>
          <Icon className="BarcodeSearchModal__close-icon" name="times" />
          <FormattedMessage {...globalTranslations.closeTitle} />
        </span>
      </Button>
    );
  };

  const getContent = () => {
    if (initState !== RequestState.NONE)
      switch (initState) {
        case ERROR_STATES.PERMISSION_DENIED_ERROR:
          return (
            <Alert color="danger">
              <FormattedMessage
                id="barcodeSearch.permissionDenied"
                defaultMessage="No permission to use camera. Please check your browser settings."
              />
            </Alert>
          );
        case ERROR_STATES.DEVICE_ERROR:
          return (
            <Alert color="danger">
              <FormattedMessage
                id="barcodeSearch.deviceError"
                defaultMessage="Camera could not be found or used."
              />
            </Alert>
          );
        default:
          return <ErrorHandler />;
      }

    return (
      <>
        <div className="BarcodeSearchModal__instructions">
          <p>
            <FormattedMessage
              id="barcodeSearch.instructions"
              defaultMessage="Barcode has to be unobstructed and in clear view. E.g. It must not be wrinkled or partially hidden."
            />
          </p>
        </div>
        <div className="BarcodeSearchModal__video-renderer">
          <div className="BarcodeSearchModal__video-mask" />
          <div className="BarcodeSearchModal__scanner-line" />
          <video id="barcode_reader_renderer" autoPlay playsInline />
        </div>
      </>
    );
  };

  // We don't want the default reactstrap styling in a simple button.
  // Reactstrap default modal close button doesn't support custom aria-label.
  const headerCloseButton = (
    <button
      aria-label={intl.formatMessage(globalTranslations.closeTitle)}
      className={classNames('BarcodeSearchModal__close-button', 'close')}
      onClick={uiStore.barcodeReaderModal.toggle}
    >
      <Icon className="BarcodeSearchModal__close-icon" name="times" />
    </button>
  );

  return (
    <Modal
      toggle={uiStore.barcodeReaderModal.toggle}
      className="BarcodeSearchModal"
      size="lg"
      isOpen={uiStore.barcodeReaderModal.isOpen}
    >
      <ModalHeader
        className="BarcodeSearchModal__header"
        close={headerCloseButton}
      >
        <FormattedMessage
          id="barcodeSearch.modalTitle"
          defaultMessage="Scan barcode"
        />
      </ModalHeader>
      <ModalBody className="BarcodeSearchModal__content">
        {getContent()}
      </ModalBody>
      <ModalFooter className="BarcodeSearchModal__footer">
        {renderCloseButton()}
      </ModalFooter>
    </Modal>
  );
};

BarcodeSearchModal.propTypes = {
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  history: RouterPropTypes.history.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  withRouter(inject('uiStore', 'routeService')(observer(BarcodeSearchModal)))
);
